import type { Map as MapboxMap } from "mapbox-gl";
import type { PostalCodesResponse, GeoJsonFeature, PostalCodeFeatureProperties } from "../../api/map/types";
import { applyIncrementalUpdate, setCachedFeatures } from "./utils";

const SOURCE_ID = "postal-codes";

/**
 * Ensures the postal codes layer exists on the map (idempotent)
 * Returns a promise that resolves when the layer is ready
 */
export const ensurePostalCodesLayer = (map: MapboxMap): Promise<void> => {
    return new Promise((resolve) => {
        console.log("[PostalCodesLayer] ensurePostalCodesLayer called, isStyleLoaded:", map.isStyleLoaded());
        
        const createLayers = () => {
            try {
                console.log("[PostalCodesLayer] Creating source and layers");

                if (!map.getSource(SOURCE_ID)) {
                    console.log("[PostalCodesLayer] Creating source:", SOURCE_ID);
                    map.addSource(SOURCE_ID, {
                        type: "geojson",
                        data: { type: "FeatureCollection", features: [] },
                        promoteId: "postalCodePolygonId"
                    });
                } else {
                    console.log("[PostalCodesLayer] Source already exists:", SOURCE_ID);
                }

                if (!map.getLayer(`${SOURCE_ID}-fill`)) {
                    console.log("[PostalCodesLayer] Creating fill layer");
                    map.addLayer({
                        id: `${SOURCE_ID}-fill`,
                        type: "fill",
                        source: SOURCE_ID,
                        paint: {
                            "fill-color": [
                                "case",
                                ["boolean", ["feature-state", "selected"], false],
                                "#60a5fa",
                                "#dbeafe"
                            ],
                            "fill-opacity": [
                                "case",
                                ["boolean", ["feature-state", "selected"], false],
                                0.5,
                                0.35
                            ]
                        }
                    });
                }

                if (!map.getLayer(`${SOURCE_ID}-outline`)) {
                    console.log("[PostalCodesLayer] Creating outline layer");
                    map.addLayer({
                        id: `${SOURCE_ID}-outline`,
                        type: "line",
                        source: SOURCE_ID,
                        paint: {
                            "line-color": [
                                "case",
                                ["boolean", ["feature-state", "selected"], false],
                                "#3b82f6",
                                "#93c5fd"
                            ],
                            "line-width": [
                                "interpolate",
                                ["linear"],
                                ["zoom"],
                                10,
                                1,
                                15,
                                1.5,
                                18,
                                2
                            ],
                            "line-opacity": 0.6
                        }
                    });
                }

                // Add labels for postal codes
                if (!map.getLayer(`${SOURCE_ID}-labels`)) {
                    console.log("[PostalCodesLayer] Creating labels layer");
                    map.addLayer({
                        id: `${SOURCE_ID}-labels`,
                        type: "symbol",
                        source: SOURCE_ID,
                        layout: {
                            "text-field": [
                                "case",
                                [">", ["coalesce", ["get", "addressCount"], 0], 0],
                                ["concat", ["get", "postalCode"], " (", ["to-string", ["get", "addressCount"]], " adr)"],
                                ["get", "postalCode"]
                            ],
                            "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                            "text-size": 11,
                            "text-anchor": "center",
                            "text-allow-overlap": false
                        },
                        paint: {
                            "text-color": "#2563eb",
                            "text-halo-color": "#ffffff",
                            "text-halo-width": 1.5
                        }
                    });
                }

                console.log("[PostalCodesLayer] Layer setup complete, resolving promise");
                resolve();
            } catch (error) {
                console.error("[PostalCodesLayer] Error creating layers:", error);
                // If style isn't loaded, wait for it
                if (!map.isStyleLoaded()) {
                    console.log("[PostalCodesLayer] Style not loaded, waiting for style.load event");
                    map.once("style.load", () => {
                        console.log("[PostalCodesLayer] style.load event fired, retrying");
                        createLayers();
                    });
                } else {
                    // Re-throw if style is loaded but we still got an error
                    throw error;
                }
            }
        };

        // Check if map style is loaded before adding sources
        if (!map.isStyleLoaded()) {
            console.log("[PostalCodesLayer] Style not loaded, waiting for style.load event");
            const styleLoadHandler = () => {
                console.log("[PostalCodesLayer] style.load event fired, creating layers");
                // Double-check that style is actually loaded
                if (map.isStyleLoaded()) {
                    createLayers();
                } else {
                    console.warn("[PostalCodesLayer] style.load fired but isStyleLoaded() still false, retrying...");
                    // Retry after a short delay
                    setTimeout(() => {
                        if (map.isStyleLoaded()) {
                            createLayers();
                        } else {
                            console.error("[PostalCodesLayer] Style still not loaded after style.load event");
                            resolve(); // Resolve anyway to avoid hanging
                        }
                    }, 100);
                }
            };
            map.once("style.load", styleLoadHandler);
            
            // Also check if style becomes loaded before the event fires
            // This can happen if style loads between the check and the event listener
            const checkInterval = setInterval(() => {
                if (map.isStyleLoaded()) {
                    console.log("[PostalCodesLayer] Style loaded detected via polling, creating layers");
                    clearInterval(checkInterval);
                    map.off("style.load", styleLoadHandler); // Remove event listener
                    createLayers();
                }
            }, 100);
            
            // Cleanup interval after 5 seconds
            setTimeout(() => {
                clearInterval(checkInterval);
            }, 5000);
        } else {
            // Style is loaded, create layers immediately
            createLayers();
        }
    });
};

/**
 * Updates the postal codes layer with data from the API
 * Uses incremental updates when possible to minimize re-rendering
 */
export const updatePostalCodesLayer = (
    map: MapboxMap,
    data: PostalCodesResponse | null,
    useIncremental: boolean = true
): void => {
    console.log("[PostalCodesLayer] updatePostalCodesLayer called:", {
        hasData: !!data,
        featureCount: data?.features.length ?? 0,
        useIncremental
    });

    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        console.warn("[PostalCodesLayer] Source not found or wrong type:", {
            hasSource: !!source,
            sourceType: source?.type
        });
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    if (!data || data.features.length === 0) {
        console.log("[PostalCodesLayer] Clearing postal codes data (no data or empty)");
        geojsonSource.setData({ type: "FeatureCollection", features: [] });
        return;
    }

    // Convert API response to Mapbox GeoJSON format
    const newFeatures: GeoJsonFeature<PostalCodeFeatureProperties>[] = data.features.map((feature) => ({
        type: "Feature" as const,
        id: feature.id ?? feature.properties.postalCodePolygonId,
        geometry: feature.geometry,
        properties: {
            ...feature.properties
        }
    }));

    console.log("[PostalCodesLayer] Converted features:", {
        count: newFeatures.length,
        firstFeature: newFeatures[0] ? {
            id: newFeatures[0].id,
            geometryType: newFeatures[0].geometry.type,
            hasGeometry: !!newFeatures[0].geometry
        } : null
    });

    // Use incremental update if enabled
    if (useIncremental) {
        try {
            applyIncrementalUpdate(
                map,
                geojsonSource,
                SOURCE_ID,
                newFeatures,
                (feature) => feature.id ?? feature.properties.postalCodePolygonId
            );
            console.log("[PostalCodesLayer] Incremental update completed");
            return;
        } catch (error) {
            // Fallback to full update on error
            console.warn("[PostalCodesLayer] Incremental update failed, falling back to full update:", error);
        }
    }

    // Full update (incremental disabled or error)
    const geojson = {
        type: "FeatureCollection" as const,
        features: newFeatures
    };

    console.log("[PostalCodesLayer] Setting GeoJSON data:", {
        featureCount: geojson.features.length,
        firstFeatureGeometry: geojson.features[0]?.geometry
    });

    geojsonSource.setData(geojson as any);
    setCachedFeatures(map, SOURCE_ID, newFeatures);
    
    console.log("[PostalCodesLayer] Data set, checking source:", {
        hasSource: !!map.getSource(SOURCE_ID),
        sourceData: (map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource)?._data
    });
};

/**
 * Removes the postal codes layer from the map
 */
export const removePostalCodesLayer = (map: MapboxMap): void => {
    if (map.getLayer(`${SOURCE_ID}-labels`)) {
        map.removeLayer(`${SOURCE_ID}-labels`);
    }
    if (map.getLayer(`${SOURCE_ID}-outline`)) {
        map.removeLayer(`${SOURCE_ID}-outline`);
    }
    if (map.getLayer(`${SOURCE_ID}-fill`)) {
        map.removeLayer(`${SOURCE_ID}-fill`);
    }
    if (map.getSource(SOURCE_ID)) {
        map.removeSource(SOURCE_ID);
    }
};

/**
 * Applies selection highlight to postal code features by setting feature-state.
 * Call after updatePostalCodesLayer when selectedIds change.
 */
export const setPostalCodesSelectionHighlight = (
    map: MapboxMap,
    selectedIds: string[]
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") return;
    const data = (source as mapboxgl.GeoJSONSource & { _data?: { features?: Array<{ id?: string; properties?: { postalCodePolygonId?: string } }> } })._data;
    if (!data?.features) return;
    const idSet = new Set(selectedIds);
    for (const f of data.features) {
        const id = f.id ?? f.properties?.postalCodePolygonId;
        if (id != null) {
            try {
                map.setFeatureState({ source: SOURCE_ID, id: String(id) }, { selected: idSet.has(String(id)) });
            } catch {
                // ignore missing feature
            }
        }
    }
};

/**
 * Sets the visibility of the postal codes layer
 */
export const setPostalCodesLayerVisibility = (map: MapboxMap, visible: boolean): void => {
    const visibility = visible ? "visible" : "none";
    if (map.getLayer(`${SOURCE_ID}-fill`)) {
        map.setLayoutProperty(`${SOURCE_ID}-fill`, "visibility", visibility);
    }
    if (map.getLayer(`${SOURCE_ID}-outline`)) {
        map.setLayoutProperty(`${SOURCE_ID}-outline`, "visibility", visibility);
    }
    if (map.getLayer(`${SOURCE_ID}-labels`)) {
        map.setLayoutProperty(`${SOURCE_ID}-labels`, "visibility", visibility);
    }
};
